import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { IMAGES } from "@/assets/images";

interface FieldItem {
  id: string;
  title: string;
  titleJa: string;
  subtitle: string;
  subtitleJa: string;
  desc: string;
  descJa: string;
  descExtra?: string;
  descExtraJa?: string;
  bg: keyof typeof IMAGES;
  tags: string[];
  color: string;
  colorMid: string;
  colorBorder: string;
  pageTitle: string;
  pageTitleJa: string;
}

const FIELDS: FieldItem[] = [
  {
    id: "ai-cinematic",
    title: "AI Cinematic",
    titleJa: "AI シネマティック",
    subtitle: "Original IP meets cinematic expression.",
    subtitleJa: "オリジナルIPとシネマティック表現の融合。",
    desc: "Original IP meets cinematic expression — merging AI-driven image-making with narrative world-building to explore the frontiers of virtual space and next-generation storytelling.",
    descJa: "AI映像生成と物語構築を組み合わせ、仮想空間の新たな可能性と次世代のストーリーテリングを追求する。",
    descExtra: "From short-form series to immersive IP universes, each project is built as a living world — where characters, lore, and visual language evolve together.",
    descExtraJa: "短編シリーズから没入型IPユニバースまで、それぞれのプロジェクトはキャラクター・世界観・映像言語が共に進化する「生きた世界」として設計される。",
    bg: "FIELDS_BG",
    tags: ["Original IP", "Short Film", "Series", "Virtual World"],
    color: "oklch(0.80 0.18 280)",
    colorMid: "oklch(0.55 0.18 280 / 0.4)",
    colorBorder: "oklch(0.65 0.18 280 / 0.45)",
    pageTitle: "Every field carries a story.",
    pageTitleJa: "すべてのフィールドは、物語を宿す。",
  },
  {
    id: "still-life",
    title: "Still Life / Objects",
    titleJa: "スティル・ライフ / オブジェクト",
    subtitle: "Objects hold time. Surfaces remember.",
    subtitleJa: "物体は時間を保持し、表面は記憶する。",
    desc: "Every object carries a history — the patina of use, the weight of memory, the silence between things. Still life photography explores the poetry hidden in the everyday: the way light falls on ceramic, the texture of worn leather, the geometry of shadow. These images ask you to slow down and truly see.",
    descJa: "静物の中に宿る詩と沈黙。日用品のパティナ、使い込まれた革の質感、影の幾何学。光と物の対話から生まれる時間の記録。見ることの深さを問いかける映像。",
    bg: "FIELDS_BG2",
    tags: ["Still Life", "Object Study", "Texture", "Gallery Works", "Product"],
    color: "oklch(0.82 0.16 60)",
    colorMid: "oklch(0.55 0.18 60 / 0.4)",
    colorBorder: "oklch(0.65 0.14 55 / 0.45)",
    pageTitle: "Every object carries a field of memory.",
    pageTitleJa: "すべての物体は、記憶のフィールドを宿す。",
  },
  {
    id: "museum-archive",
    title: "Museum / Archive",
    titleJa: "ミュージアム・アーカイブ",
    subtitle: "Dignity and innovation for inherited memory.",
    subtitleJa: "継いでいく記憶と伝統に尊厳と革新を。",
    desc: "Museums and archives are living organisms — they breathe with every new acquisition, every reinterpretation of the past. Visual documentation for cultural institutions, exhibition catalogues, and heritage projects. Where the act of preservation becomes itself an act of creation, honoring tradition while imagining its future.",
    descJa: "博物館・アーカイブ施設の収蔵品撮影、展覧カタログ制作、文化財の記録映像。伝統を継承しながら未来を想像する、保存という創造行為。",
    bg: "FIELDS_BG3",
    tags: ["Museum Video", "Cultural Archive", "Educational Film", "Exhibition Catalog", "Documentary"],
    color: "oklch(0.78 0.16 195)",
    colorMid: "oklch(0.50 0.16 195 / 0.4)",
    colorBorder: "oklch(0.62 0.13 195 / 0.45)",
    pageTitle: "Every archive carries a field of culture.",
    pageTitleJa: "すべてのアーカイブは、文化のフィールドを宿す。",
  },
  {
    id: "education",
    title: "Education",
    titleJa: "エデュケーション",
    subtitle: "As the means evolve, we document with passion.",
    subtitleJa: "伝えていく手段が時代とともに変わるように、情熱をもって記録にとりくむ。",
    desc: "As the means of transmission evolve, we document with passion — thinking through a folkloristic lens. Education foundations, academic institutions, and NGOs. From documentary portraits of teachers and communities to video works that give motion to ancient knowledge.",
    descJa: "民俗学的な思考を大切にして、映像の視点を考える。教育財団・学術機関・NGO向けの映像制作。教師・研究者・コミュニティのドキュメンタリーポートレートから、古い知識に新しい動きをもたらす映像まで。",
    bg: "FIELDS_BG4",
    tags: ["Documentary", "Interview Video", "Institutional Video"],
    color: "oklch(0.80 0.16 140)",
    colorMid: "oklch(0.52 0.15 140 / 0.4)",
    colorBorder: "oklch(0.65 0.15 140 / 0.45)",
    pageTitle: "Every lesson carries a field of future.",
    pageTitleJa: "すべての学びは、未来のフィールドを宿す。",
  },
];

export function FieldsHorizontal({ onBgChange }: { onBgChange?: (bg: string) => void }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isAuto, setIsAuto] = useState(true);
  const [manualStop, setManualStop] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const count = FIELDS.length;

  const goTo = useCallback(
    (idx: number) => {
      const next = ((idx % count) + count) % count;
      setActiveIdx(next);
      if (onBgChange) onBgChange(IMAGES[FIELDS[next].bg] as string);
    },
    [count, onBgChange]
  );

  const stopAuto = useCallback(() => {
    setIsAuto(false);
    setManualStop(true);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    if (!manualStop) {
      timerRef.current = setInterval(() => {
        setActiveIdx((prev) => {
          const next = (prev + 1) % count;
          if (onBgChange) onBgChange(IMAGES[FIELDS[next].bg] as string);
          return next;
        });
      }, 5000);
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [manualStop, count, onBgChange]);

  // resume auto after 8s of manual stop
  useEffect(() => {
    if (manualStop) {
      const t = setTimeout(() => setManualStop(false), 8000);
      return () => clearTimeout(t);
    }
  }, [manualStop]);

  const handlePrev = () => { stopAuto(); goTo(activeIdx - 1); };
  const handleNext = () => { stopAuto(); goTo(activeIdx + 1); };

  const activeField = FIELDS[activeIdx];
  const leftIdx = (activeIdx - 1 + count) % count;
  const rightIdx = (activeIdx + 1) % count;
  const cards = [
    { idx: leftIdx, pos: -1 },
    { idx: activeIdx, pos: 0 },
    { idx: rightIdx, pos: 1 },
  ];

  return (
    <section
      id="fields"
      style={{
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        background: "transparent",
      }}
    >
      {/* Background — switches per active card with crossfade */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeField.bg}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${IMAGES[activeField.bg]})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            zIndex: 0,
          }}
        />
      </AnimatePresence>

      {/* Dark gradient overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.18) 40%, rgba(0,0,0,0.60) 100%)",
          zIndex: 1,
        }}
      />

      {/* Page title (changes per card) */}
      <motion.div
        style={{ position: "relative", zIndex: 10, textAlign: "center", marginBottom: "clamp(20px, 3vw, 40px)", padding: "0 16px" }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={activeField.id + "-title"}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.5 }}
          >
            <p
              style={{
                fontSize: "clamp(18px, 3vw, 42px)",
                fontWeight: 300,
                color: "#fff",
                letterSpacing: "0.06em",
                fontFamily: "'Rajdhani', sans-serif",
                textShadow: "0 2px 16px rgba(0,0,0,0.8)",
                marginBottom: "4px",
              }}
            >
              {activeField.pageTitle}
            </p>
            <p style={{ fontSize: "clamp(10px, 1.1vw, 14px)", color: "rgba(255,255,255,0.55)", letterSpacing: "0.08em" }}>
              {activeField.pageTitleJa}
            </p>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Cards + Buttons */}
      <div style={{ position: "relative", zIndex: 10, width: "100%" }}>
        {/* Cards row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "clamp(10px, 2vw, 24px)",
            padding: "20px clamp(56px, 8vw, 140px)",
            overflow: "visible",
          }}
        >
          {cards.map(({ idx, pos }) => {
            const field = FIELDS[idx];
            const isActive = pos === 0;
            return (
              <FieldCard
                key={field.id}
                field={field}
                isActive={isActive}
                onClick={() => { stopAuto(); goTo(idx); }}
              />
            );
          })}
        </div>

        {/* Left button */}
        <button
          onClick={handlePrev}
          aria-label="Previous field"
          style={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            left: "clamp(8px, 4vw, 60px)",
            zIndex: 20,
            background: "rgba(10,10,20,0.70)",
            border: "1px solid rgba(255,255,255,0.18)",
            borderRadius: "50%",
            width: "clamp(36px, 4.5vw, 52px)",
            height: "clamp(36px, 4.5vw, 52px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            backdropFilter: "blur(6px)",
            color: "#fff",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(80,140,255,0.35)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(10,10,20,0.70)"; }}
        >
          <ChevronLeft style={{ width: "clamp(16px, 2vw, 22px)", height: "clamp(16px, 2vw, 22px)" }} />
        </button>

        {/* Right button */}
        <button
          onClick={handleNext}
          aria-label="Next field"
          style={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            right: "clamp(8px, 4vw, 60px)",
            zIndex: 20,
            background: "rgba(10,10,20,0.70)",
            border: "1px solid rgba(255,255,255,0.18)",
            borderRadius: "50%",
            width: "clamp(36px, 4.5vw, 52px)",
            height: "clamp(36px, 4.5vw, 52px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            backdropFilter: "blur(6px)",
            color: "#fff",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(80,140,255,0.35)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(10,10,20,0.70)"; }}
        >
          <ChevronRight style={{ width: "clamp(16px, 2vw, 22px)", height: "clamp(16px, 2vw, 22px)" }} />
        </button>
      </div>

      {/* Dots + AUTO/CLICK */}
      <div style={{ position: "relative", zIndex: 10, display: "flex", alignItems: "center", gap: "10px", marginTop: "clamp(16px, 2vw, 28px)" }}>
        <button
          onClick={() => { setManualStop((s) => !s); setIsAuto((a) => !a); }}
          style={{
            fontSize: "9px",
            letterSpacing: "0.15em",
            color: manualStop ? "rgba(255,120,80,0.75)" : "rgba(140,220,255,0.65)",
            border: manualStop ? "1px solid rgba(255,120,80,0.35)" : "1px solid rgba(140,220,255,0.25)",
            borderRadius: "100px",
            padding: "2px 8px",
            background: "transparent",
            cursor: "pointer",
            transition: "all 0.3s",
            minWidth: "52px",
            textAlign: "center",
          }}
        >
          {manualStop ? "CLICK" : "AUTO"}
        </button>
        {FIELDS.map((_, idx) => (
          <button
            key={idx}
            onClick={() => { stopAuto(); goTo(idx); }}
            aria-label={`Go to field ${idx + 1}`}
            style={{
              width: idx === activeIdx ? "24px" : "8px",
              height: "8px",
              borderRadius: "100px",
              background: idx === activeIdx ? "rgba(255,255,255,0.90)" : "rgba(255,255,255,0.30)",
              border: "none",
              cursor: "pointer",
              transition: "all 0.35s ease",
              padding: 0,
            }}
          />
        ))}
      </div>
    </section>
  );
}

// Individual field card component
function FieldCard({
  field,
  isActive,
  onClick,
}: {
  field: FieldItem;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <motion.div
      layout
      animate={{
        scale: isActive ? 1 : 0.82,
        opacity: isActive ? 1 : 0.5,
      }}
      transition={{ duration: 0.45, ease: "easeInOut" }}
      onClick={isActive ? undefined : onClick}
      style={{
        width: isActive ? "clamp(320px, 68vw, 680px)" : "clamp(160px, 22vw, 300px)",
        height: isActive ? "clamp(420px, 55vh, 580px)" : "clamp(280px, 38vh, 400px)",
        cursor: isActive ? "default" : "pointer",
        overflow: "hidden",
        borderRadius: 20,
        border: `1.5px solid ${field.colorBorder}`,
        background: isActive
          ? `linear-gradient(160deg, ${field.colorMid} 0%, rgba(0,0,0,0.55) 100%)`
          : `linear-gradient(160deg, ${field.colorMid} 0%, rgba(0,0,0,0.65) 100%)`,
        backdropFilter: "blur(2px)",
        boxShadow: isActive
          ? `0 0 40px ${field.color.replace(")", " / 0.18)")}, 0 8px 40px rgba(0,0,0,0.5)`
          : "0 4px 20px rgba(0,0,0,0.35)",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: "relative",
          zIndex: 2,
          padding: isActive ? "clamp(24px, 4vw, 40px)" : "20px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
        }}
      >
        {isActive ? (
          <AnimatePresence mode="wait">
            <motion.div
              key={field.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <p style={{ fontSize: "0.7rem", color: field.color, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 6 }}>
                {field.titleJa}
              </p>
              <h3
                style={{
                  fontSize: "clamp(1.4rem, 3.5vw, 2.2rem)",
                  fontWeight: 700,
                  color: "#fff",
                  textShadow: "0 2px 12px rgba(0,0,0,0.7)",
                  marginBottom: 8,
                  lineHeight: 1.2,
                }}
              >
                {field.title}
              </h3>
              <p style={{ fontSize: "clamp(0.85rem, 1.8vw, 1.1rem)", color: "rgba(255,255,255,0.85)", lineHeight: 1.5, marginBottom: 4 }}>
                {field.subtitle}
              </p>
              <p style={{ fontSize: "clamp(0.72rem, 1.2vw, 0.88rem)", color: "rgba(255,255,255,0.55)", lineHeight: 1.5, marginBottom: 8 }}>
                {field.subtitleJa}
              </p>
              <p style={{ fontSize: "clamp(0.78rem, 1.3vw, 0.92rem)", color: "rgba(255,255,255,0.7)", lineHeight: 1.65, marginBottom: 4 }}>
                {field.desc}
              </p>
              {field.descExtra && (
                <p style={{ fontSize: "clamp(0.7rem, 1.1vw, 0.82rem)", color: "rgba(255,255,255,0.45)", lineHeight: 1.55, marginBottom: 12, fontStyle: "italic" }}>
                  {field.descExtra}
                </p>
              )}
              <p style={{ fontSize: "clamp(0.65rem, 1vw, 0.76rem)", color: "rgba(200,230,255,0.45)", lineHeight: 1.55, marginBottom: 16 }}>
                {field.descJa}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {field.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      fontSize: "0.65rem",
                      padding: "2px 10px",
                      borderRadius: 9999,
                      border: `1px solid ${field.colorBorder}`,
                      color: field.color,
                      background: "rgba(0,0,0,0.3)",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        ) : (
          <div>
            <p style={{ fontSize: "0.7rem", color: field.color, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 6 }}>
              {field.titleJa}
            </p>
            <h3 style={{ fontSize: "clamp(1rem, 2.5vw, 1.4rem)", color: "#fff", fontWeight: 700, textShadow: "0 2px 8px rgba(0,0,0,0.8)" }}>
              {field.title}
            </h3>
          </div>
        )}
      </div>
    </motion.div>
  );
}
